import React, {useCallback, useContext, useMemo, useState} from 'react';
import './_RecipeIngredientFormModa.scss';
import {
    ingredientTypes, initializeFormFromIngredient,
    prepareIngredientFormForServer
} from "../../../../../../utils/modelUtils/orozco/orozcoRecipeIngredientUtils";
import ClassicModal from "../../../../../../components/Modals/ClassicModal";
import TideEntitySelect from "../../../../../../components/TideEntitySelect/TideEntitySelect";
import useFormState from "../../../../../../hooks/useFormState";
import {productUnitConversionSGroups} from "../../../../../../utils/modelUtils/almazen/productsUtils";
import {getUnitsArray} from "../../../../../../utils/modelUtils/almazen/unitsUtility";
import _ from 'lodash';
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {TideApiContext} from "../../../../../../api/tideApiConfig";
import {NotifierContext} from "../../../../../../utils/Notifier";
import {useSelector} from "react-redux";
import {orozcoRecipeUnitConversionSGroups} from "../../../../../../utils/modelUtils/orozco/orozcoRecipeUtils";

const filters={isActive: true};

const RecipeIngredientFormModal = ({onHide, recipe, onSave, ingredient}) => {


    const api = useContext(TideApiContext);
    const notifier = useContext(NotifierContext);
    const [type, setType] = useState(ingredient?.product? ingredientTypes.product : ingredient?.orozcoRecipe? ingredientTypes.recipe : '');
    const {form, handleSimpleChange, handleInputChange} = useFormState(initializeFormFromIngredient(ingredient));

    // ------- handle form changes ---------
    //Filter for selecting the ingredient from the server
    const additionalFilters = useMemo(()=>{
        return {
            ...filters,
            sGroups:type===ingredientTypes.product?
                productUnitConversionSGroups:orozcoRecipeUnitConversionSGroups
        }
    },[type]);

    const mainIngredient = form[ingredientTypes.product] || form[ingredientTypes.recipe];
    const units = (mainIngredient && getUnitsArray(mainIngredient))||[];

    const selectedUnit = _.find(units, u=>`${u.id}`===`${form.unit}`);
    const totalQuantity = Number(form.quantity) || 0;
    const shrinkagePercentage = Number(form.expectedShrinkage) || 0;
    const isShrinkageValid = shrinkagePercentage >= 0 && shrinkagePercentage <= 100;
    const expectedShrinkage = isShrinkageValid
        ? totalQuantity * (shrinkagePercentage / 100)
        : null;
    const usableQuantity = isShrinkageValid
        ? totalQuantity - expectedShrinkage
        : null;

    // ----  Save edition or new ingredient ---------
    const loadingId = 'RecipeIngredientFormModal.orozcoRecipes';
    const handleSave = useCallback(()=>{
        try {
            const preparedIngredient = prepareIngredientFormForServer(form, type);

            const orozcoRecipeIngredients = (recipe.orozcoRecipeIngredients||[]).map(i=>i.id);
            orozcoRecipeIngredients.push(preparedIngredient);

            api.orozcoRecipes.update({id: recipe.id, params:{orozcoRecipeIngredients}, loadingId})
                .then(onSave);
        }
        catch (e){
            notifier.error(e.message);
        }
    },[api, recipe, form, type, notifier, onSave]);

    const loading = useSelector(s=>!!s.loadingIds[loadingId]);

    // ----- handle deletion ----------
    const handleDelete = ()=>{
        api.orozcoRecipeIngredients.delete({id: ingredient.id, loadingId})
            .then(onSave);
    };

    return (
        <ClassicModal
            className={"RecipeIngredientFormModal"}
            title={'Ingrediente de '+recipe.name}
            onHide={onHide}
            onSave={handleSave}
            disabled={loading}
        >
            <div className='row'>
                <div className='col-xs-12'>
                    <p>¿Es un producto o una sub-receta?</p>
                    <select className='form-control' value={type} onChange={e=>setType(e.target.value)}>
                        <option value='' disabled>Selecciona tipo</option>
                        <option value={ingredientTypes.product}>Producto</option>
                        <option value={ingredientTypes.recipe}>Receta</option>
                    </select>
                </div>
            </div>

            {type &&
            <div>
                <div className='row'>
                    <div className='col-xs-12'>
                        <p>Selecciona {type===ingredientTypes.recipe?'la receta':'el producto'}</p>
                        <TideEntitySelect
                            entity={type+'s'}
                            value={form[type]}
                            onChange={handleSimpleChange(type)}
                            additionalFilters={additionalFilters}
                        />
                    </div>
                </div>
                <div className='row'>
                    <div className='col-sm-6'>
                        <p>Cantidad</p>
                        <input className='form-control'
                            value={form.quantity||''}
                            onChange={handleInputChange('quantity')}
                        />
                    </div>
                    <div className='col-sm-6'>
                        <p>Unidad</p>
                        <select className='form-control'
                            value={form.unit||''}
                            onChange={handleInputChange('unit')}
                        >
                            <option value='' disabled=''>Selecciona una unidad</option>
                            {units.map(u=><option value={u.id} key={u.id}>{u.name}</option>)}
                        </select>

                    </div>
                </div>

                <div className='row'>
                    <div className='col-sm-6'>
                        <p>
                            Merma (%)
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip id={1}>
                                    La merma forma parte de la cantidad total utilizada. Por ejemplo, si se configuran 200 gramos de
                                    producto y 50% de merma, 100 gramos corresponden a merma y 100 gramos son aprovechables.
                                    El gasto total de inventario permanece en 200 gramos.
                                </Tooltip>}>
                                <span><i className='fa fa-question-circle'/></span>
                            </OverlayTrigger>
                        </p>
                        <input className='form-control'
                            value={form.expectedShrinkage||''}
                            onChange={handleInputChange('expectedShrinkage')}
                        />
                    </div>
                    <div className='col-sm-6'>
                        <p>=</p>
                        <p>{expectedShrinkage !== null ? `${expectedShrinkage} ${selectedUnit?.name || ''}` : '—'}</p>
                    </div>
                </div>
                <hr/>
                <div className='row'>
                    <div className='col-sm-6'>
                        <p>Cantidad aprovechable</p>
                    </div>
                    <div className='col-sm-6'>
                        <p>{usableQuantity !== null ? `${usableQuantity} ${selectedUnit?.name || ''}` : '—'}</p>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-sm-6'>
                        <p>Gasto total de {form[type]?.name}</p>
                    </div>
                    <div className='col-sm-6'>
                        <p>{totalQuantity} {selectedUnit?.name}</p>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-xs-12'>
                        <p className='small muted'>
                            La merma forma parte de la cantidad total utilizada y no se suma como consumo adicional.
                        </p>
                    </div>
                </div>

                {ingredient.id && <>
                    <hr/>
                    <button className='btn btn-sm btn-danger' onClick={handleDelete} disabled={loading}>Eliminar ingrediente</button>
                </>}

            </div>}

        </ClassicModal>
    );
};

export default RecipeIngredientFormModal;
